/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import DateRangeBar, { fmt } from '../active-users/DateRangeBar';
import UserDetailTabs, { TABS, DATE_DRIVEN } from './UserDetailTabs';
import { readSavedFilters, usePersistFilters } from '../../hooks/filterPersistence';
import IdentityCard from './IdentityCard';
import GrantAccessModal from '../free-grants/GrantAccessModal';
import createGrantApi, { grantedMessage } from '../free-grants/grantApi';
import HeartRateTab from './tabs/HeartRateTab';
import PausesTab from './tabs/PausesTab';
import StandingChecksTab from './tabs/StandingChecksTab';
import SleepTab from './tabs/SleepTab';
import BioVitalsTab from './tabs/BioVitalsTab';
import MonitorSessionsTab from './tabs/MonitorSessionsTab';
import MedicineTab from './tabs/MedicineTab';
import SubscriptionTab from './tabs/SubscriptionTab';
import AppAccessTab from './tabs/AppAccessTab';
import FreeAccessTab from './tabs/FreeAccessTab';
import ProfileTab from './tabs/ProfileTab';
import '../active-users/active-users.css';
import '../free-grants/free-grants.css';
import './user-detail.css';

// Guarded decode: atob throws on malformed base64, and an uncaught throw during render
// blanks the page. A bad id resolves to '' → the "user not found" branch (never a crash).
const decodeUserId = (encoded) => { try { return atob(encoded || ''); } catch { return ''; } };

// Remember the last-active main tab so reopening this screen from anywhere restores it.
const ACTIVE_TAB_KEY = 'userDetailActiveTab';
const DEFAULT_TAB = 'heart-rate';
const isTab = (key) => TABS.some((t) => t.key === key);

// `?tab=` wins over the remembered tab: a link that names a tab (e.g. the Users list
// Activity Count cell) must land on it regardless of what was open here last.
const resolveInitialTab = (requested) => {
  if (isTab(requested)) return requested;
  const saved = readSavedFilters(ACTIVE_TAB_KEY);
  return isTab(saved) ? saved : DEFAULT_TAB;
};

// A deep link may also pin the window; an invalid/absent date leaves the page all-time (046).
const toDateObj = (s) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s || '')) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const UserDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { encodedUserId } = useParams();
  const userId = decodeUserId(encodedUserId);

  // Deep-link params (read once on mount — they seed state, they do not drive it).
  const [linked] = useState(() => {
    const qs = new URLSearchParams(location.search);
    return {
      tab: qs.get('tab') || '',
      eventType: qs.get('event_type') || '',
      from: qs.get('from_date') || '',
      to: qs.get('to_date') || '',
    };
  });

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');

  const [profileStatus, setProfileStatus] = useState('loading'); // loading | ok | notfound | error
  const [user, setUser] = useState(null);

  const [active, setActive] = useState(() => resolveInitialTab(linked.tab));
  const [activated, setActivated] = useState(() => new Set([resolveInitialTab(linked.tab)]));
  usePersistFilters(ACTIVE_TAB_KEY, active);

  // Detail pages open all-time (feature 046); the shared control narrows the window.
  const [preset, setPreset] = useState(() => (linked.from || linked.to ? 'custom' : null));
  const [displayFrom, setDisplayFrom] = useState(() => toDateObj(linked.from));
  const [displayTo, setDisplayTo] = useState(() => toDateObj(linked.to));
  const [range, setRange] = useState(() => ({ from: linked.from, to: linked.to }));

  // Free access grant (065): whether the modal is open for this member, and the last outcome.
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantNotice, setGrantNotice] = useState('');
  // Bumped after every grant. The Free access tab stays mounted once opened (that is the whole
  // point of the lazy-mount scheme), so remounting it is what makes a new grant appear there
  // without the operator reloading the page.
  const [grantNonce, setGrantNonce] = useState(0);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => { refreshToken(); }, []);
  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => { if (token) loadProfile(); }, [token]);

  const loadProfile = async () => {
    if (!userId) { setProfileStatus('notfound'); return; }
    setProfileStatus('loading');
    try {
      const res = await axiosJWT.get(
        `${baseUrl}/admin/api/user-profile?user_id=${encodeURIComponent(userId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser(res.data.data);
      setProfileStatus('ok');
    } catch (err) {
      const s = err?.response?.status;
      if (s === 404 || s === 400) setProfileStatus('notfound');
      else if (s === 401) setProfileStatus('auth');
      else setProfileStatus('error');
    }
  };

  // Data requests go through the JWT instance with the current bearer token.
  const authGet = (path) => axiosJWT.get(`${baseUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });

  // The grant modal's server adapter — the same one /users and the survey tabs hand it.
  const grantApi = createGrantApi(axiosJWT, token, baseUrl);

  const handleGranted = (grant) => {
    setGrantOpen(false);
    setGrantNotice(grantedMessage(grant));
    // Plan and Status on the card are server-derived, so neither is right until we refetch.
    loadProfile();
    setGrantNonce((n) => n + 1);
  };

  const selectTab = (key) => {
    setActive(key);
    setActivated((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  };

  // ---- date range (presets commit immediately; manual edits commit on Search) ----
  const onPreset = (key, fromStr, toStr) => {
    setPreset(key);
    setDisplayFrom(fromStr ? new Date(fromStr) : null);
    setDisplayTo(toStr ? new Date(toStr) : null);
    setRange({ from: fromStr, to: toStr });
  };
  const onFromChange = (d) => { setPreset('custom'); setDisplayFrom(d); };
  const onToChange = (d) => { setPreset('custom'); setDisplayTo(d); };
  const onSearch = () => setRange({ from: displayFrom ? fmt(displayFrom) : '', to: displayTo ? fmt(displayTo) : '' });
  const onClear = () => { setPreset(null); setDisplayFrom(null); setDisplayTo(null); setRange({ from: '', to: '' }); };
  const hasRange = !!(range.from || range.to || displayFrom || displayTo);

  const tabProps = { userId, from: range.from, to: range.to, authGet };
  const renderTab = (key) => {
    switch (key) {
      case 'heart-rate': return <HeartRateTab {...tabProps} />;
      case 'pauses': return <PausesTab {...tabProps} />;
      case 'standing': return <StandingChecksTab {...tabProps} />;
      case 'sleep': return <SleepTab {...tabProps} />;
      case 'bio-vitals': return <BioVitalsTab {...tabProps} />;
      case 'monitor': return <MonitorSessionsTab {...tabProps} />;
      case 'medicine': return <MedicineTab {...tabProps} />;
      case 'app-access': return <AppAccessTab {...tabProps} initialEventType={linked.eventType} />;
      // Lifecycle, not window-driven → no from/to, and it re-reads after a grant is made.
      case 'free-access': return <FreeAccessTab key={`fa-${grantNonce}`} userId={userId} authGet={authGet} />;
      case 'profile': return <ProfileTab user={user} />;
      case 'subscription': return <SubscriptionTab userId={userId} authGet={authGet} />;
      default: return null;
    }
  };

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">People / Users / <b>{user?.name || 'Detail'}</b></span>
            <span className="au-route">/user-detail</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/users">← Back to users</Link></span>
          </div>

          {profileStatus === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : profileStatus === 'error' ? (
            <ErrorState message="We couldn’t load this user. Please try again." onRetry={loadProfile} />
          ) : profileStatus === 'notfound' ? (
            <div className="ud-notfound" data-testid="ud-notfound">
              <EmptyState
                title="User not found"
                icon="🔍"
                message="This user does not exist or has been deleted."
                hasFilters={false}
              />
              <Link to="/users" className="au-btn au-btn--ghost">← Back to users</Link>
            </div>
          ) : profileStatus === 'loading' ? (
            <div className="au-panel"><TableSkeleton rows={6} /></div>
          ) : (
            <div className="ud-layout">
              <IdentityCard
                user={user}
                onGrantAccess={() => setGrantOpen(true)}
                onShowAllFields={() => selectTab('profile')}
              />
              <div className="ud-content-col">
                {grantNotice && (
                  <div className="fg-alert fg-alert--ok ud-grant-notice" role="status" data-testid="ud-grant-notice">
                    {grantNotice}
                    <button type="button" className="fg-btn" onClick={() => setGrantNotice('')}>
                      Dismiss
                    </button>
                  </div>
                )}
                <UserDetailTabs active={active} onSelect={selectTab} />
                {DATE_DRIVEN.has(active) && (
                  <div style={{ marginBottom: 14 }}>
                    <DateRangeBar
                      preset={preset}
                      displayFrom={displayFrom}
                      displayTo={displayTo}
                      hasRange={hasRange}
                      onPreset={onPreset}
                      onFromChange={onFromChange}
                      onToChange={onToChange}
                      onSearch={onSearch}
                      onClear={onClear}
                    />
                  </div>
                )}
                {TABS.filter((t) => activated.has(t.key)).map((t) => (
                  <div key={t.key} style={{ display: active === t.key ? 'block' : 'none' }} data-testid={`ud-panel-${t.key}`}>
                    {renderTab(t.key)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {grantOpen && user && (
        <GrantAccessModal
          user={user}
          api={grantApi}
          onClose={() => setGrantOpen(false)}
          onGranted={handleGranted}
        />
      )}
    </div>
  );
};

export default UserDetail;
